import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import './ContactPage.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';


const API = process.env.REACT_APP_BACKEND_URL;


const ContactPage = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME;
  const companyPhone = process.env.REACT_APP_COMPANY_PHONE;
  const companyEmail = process.env.REACT_APP_COMPANY_EMAIL;
  const companyAddress = process.env.REACT_APP_COMPANY_ADDRESS;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.subject) {
    toast.error('Please select a subject');
    return;
  }

  setIsSubmitting(true);

  try {
    const response = await axios.post(API, formData, {
      headers: { "Content-Type": "application/json" }
    });

    if (response.data.success) {
      toast.success("Message sent successfully!");
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    } else {
      toast.error(response.data.error || "Failed to send message.");
    }

  } catch (error) {
    console.error("Error submitting:", error);
    toast.error("Server error. Unable to send message.");
  } finally {
    setIsSubmitting(false);
  }
};


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>
        <div className="contact-hero-content">
          <h1>Get In Touch</h1>
          <p>Connect with our team to discuss your energy needs and partnership opportunities</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Contact Information</h2>
              <p className="contact-intro">
                Our team is ready to assist you with inquiries about our services, operations, partnerships, and career opportunities.
              </p>

              <div className="contact-details">
                <div className="contact-detail">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div className="contact-detail-content">
                    <h4>Phone</h4>
                    <a href={`tel:${companyPhone}`}>{companyPhone}</a>
                    <p>Mon-Fri, 8:00 AM - 6:00 PM CST</p>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <div className="contact-detail-content">
                    <h4>Email</h4>
                    <a href={`mailto:${companyEmail}`}>{companyEmail}</a>
                    <p>24/7 Email Support</p>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="contact-detail-content">
                    <h4>Office Address</h4>
                    <p>{companyAddress}</p>
                  </div>
                </div>
              </div>

              <div className="business-hours">
                <h3>Business Hours</h3>
                <div className="hours-list">
                  <div className="hours-item">
                    <span>Monday - Friday</span>
                    <span>8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="hours-item">
                    <span>Saturday</span>
                    <span>9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="hours-item">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <h2>Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john.doe@example.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="company">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="subject">Subject *</Label>
                  <Select name="subject" value={formData.subject} onValueChange={(value) => setFormData({...formData, subject: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select inquiry type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                      <SelectItem value="services">Services Information</SelectItem>
                      <SelectItem value="careers">Career Opportunities</SelectItem>
                      <SelectItem value="media">Media Inquiry</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Please provide details about your inquiry..."
                  />
                </div>

                <div className="form-actions">
                  <Button type="submit" className="submit-btn" disabled={isSubmitting}>
                    <Send size={18} />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="map-section">
        <div className="map-container">
          <iframe
            src="
			https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2445.865020335831!2d16.929055076974386!3d52.40775567201204!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47045b384f311d99%3A0x71d920ae2dba0837!2sPiekary%2019%2F601%2C%2061-823%20Pozna%C5%84%2C%20Poland!5e0!3m2!1sen!2s!4v{{timestamp}}"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Office Location"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;